import React, { useState } from "react";
import { ChevronLeft, ChevronRight, Check, Shirt, Calculator, Trash2 } from "lucide-react";
import PickerWithAdd from "@/components/PickerWithAdd";
import TShirtDiagram from "@/components/TShirtDiagram";
import PricingCalculator from "@/components/PricingCalculator";
import { inr } from "@/lib/api";

const SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"];
const OVERSIZE_KEYS = ["3XL", "4XL", "5XL"];
const COMMON_COLORS = ["Black", "White", "Navy", "Grey", "Red", "Blue", "Green", "Maroon", "Beige", "Yellow"];
const CUSTOMIZATION_TYPES = ["", "Embroidery", "DTF Print", "Screen Print", "UV Print", "Laser Engraving", "Plain (no customization)"];

const STEPS = [
  { key: "product", label: "Product" },
  { key: "color", label: "Color" },
  { key: "sizes", label: "Sizes" },
  { key: "pricing", label: "Pricing" },
  { key: "custom", label: "Customization" },
  { key: "review", label: "Review" },
];

/**
 * Mobile-only stepped wizard for editing ONE line item.
 * Field order mirrors user spec: Product → Color → Sizes (+ Other size) → Qty →
 * Customization → Other customization (text + cost) → HSN → GST → Line total.
 * On desktop, the parent should render the flat inline row instead of this.
 */
export default function MobileLineWizard({
  index,
  item,
  productOpts,
  onProductPick,
  onColorCreate,
  setField,
  setSize,
  onRemove,
  removable,
  oversizeCount,
}) {
  const [step, setStep] = useState(0);
  const [showCalc, setShowCalc] = useState(false);
  const [showTee, setShowTee] = useState(false);

  const lineTotal = (Number(item.qty) || 0) * ((Number(item.unit_price) || 0) + (Number(item.customization_price) || 0) + (Number(item.other_customization_cost) || 0)) + (Number(item.oversize_surcharge) || 0) * oversizeCount(item);

  const totalSizes = Object.values(item.sizes || {}).reduce((a, b) => a + Number(b || 0), 0);
  const stepValid = (() => {
    if (STEPS[step].key === "product") return !!item.product;
    if (STEPS[step].key === "sizes") return totalSizes > 0 || Number(item.qty) > 0;
    return true;
  })();

  const next = () => setStep(s => Math.min(STEPS.length - 1, s + 1));
  const back = () => setStep(s => Math.max(0, s - 1));

  return (
    <div className="border border-[#EAE5DB] rounded-md bg-[#FBF8F1] overflow-hidden" data-testid={`wizard-line-${index}`}>
      {/* Progress bar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#EAE5DB] bg-white">
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          {STEPS.map((s, i) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setStep(i)}
              className={`text-[10px] px-2 py-1 rounded-full whitespace-nowrap ${i === step ? "bg-[#C45A41] text-white" : i < step ? "bg-[#DDEADE] text-[#2E4A38]" : "bg-[#F3ECE1] text-[#75706A]"}`}
              data-testid={`wizard-step-${index}-${s.key}`}
            >
              {i + 1}. {s.label}
            </button>
          ))}
        </div>
        {removable && (
          <button type="button" onClick={onRemove} className="p-1 text-[#B83A3A] hover:bg-[#F7DFDF] rounded ml-2" data-testid={`wizard-remove-${index}`}>
            <Trash2 size={14} />
          </button>
        )}
      </div>

      <div className="p-4 space-y-3 min-h-[220px]">
        {STEPS[step].key === "product" && (
          <>
            <div className="label-tiny mb-1">Product *</div>
            <PickerWithAdd
              testid={`wizard-product-${index}`}
              value={item.product}
              onChange={(v, meta) => { setField("product", v); if (meta) onProductPick(meta); }}
              options={productOpts}
              placeholder="Choose product"
              addLabel="product"
              onCreate={onColorCreate.forProduct}
            />
            <p className="text-[10px] text-[#75706A] mt-2">Picking a product auto-fills its default price, HSN, GST slab and 3XL+ upcharge — all still editable in later steps.</p>
          </>
        )}

        {STEPS[step].key === "color" && (
          <>
            <div className="label-tiny mb-1">Color</div>
            <PickerWithAdd
              testid={`wizard-color-${index}`}
              value={item.color}
              onChange={v => setField("color", v)}
              options={COMMON_COLORS.map(c => ({ value: c, label: c }))}
              placeholder="Fabric colour"
              addLabel="color"
              onCreate={onColorCreate.forColor}
            />
          </>
        )}

        {STEPS[step].key === "sizes" && (
          <>
            <div className="label-tiny mb-1">Size-wise quantity</div>
            <div className="grid grid-cols-4 gap-1.5">
              {SIZES.map(sz => (
                <div key={sz}>
                  <div className="text-[10px] text-center text-[#757169] mb-0.5 font-medium">{sz}</div>
                  <input
                    type="number" min="0"
                    className="input-field text-center px-1"
                    value={item.sizes?.[sz] ?? ""} placeholder="—"
                    data-testid={`wizard-size-${index}-${sz}`}
                    onChange={e => setSize(sz, e.target.value)}
                  />
                </div>
              ))}
            </div>

            <div className="mt-3">
              <div className="label-tiny mb-1">Other size <span className="normal-case text-[10px]">(free-text: kids XS, 26w×32l…)</span></div>
              <input
                className="input-field"
                placeholder='e.g. "kids XS", "26w × 32l"'
                value={item.other_size || ""}
                onChange={e => setField("other_size", e.target.value)}
                data-testid={`wizard-other-size-${index}`}
              />
            </div>

            <div className="mt-3">
              <div className="label-tiny mb-1">Total quantity <span className="normal-case text-[10px]">(auto-sums from sizes above)</span></div>
              <input
                type="number"
                className="input-field"
                value={item.qty || ""}
                onChange={e => setField("qty", Number(e.target.value) || 0)}
                data-testid={`wizard-qty-${index}`}
              />
            </div>
          </>
        )}

        {STEPS[step].key === "custom" && (
          <>
            <div className="label-tiny mb-1">Customization type</div>
            <select
              className="input-field"
              value={item.customization}
              onChange={e => setField("customization", e.target.value)}
              data-testid={`wizard-cust-type-${index}`}
            >
              {CUSTOMIZATION_TYPES.map(c => <option key={c} value={c}>{c || "— none —"}</option>)}
            </select>

            <div className="grid grid-cols-2 gap-2 mt-3">
              <div>
                <div className="label-tiny mb-1">Cust ₹/pc</div>
                <input type="number" className="input-field"
                  value={item.customization_price ?? 0}
                  onChange={e => setField("customization_price", Number(e.target.value) || 0)}
                  data-testid={`wizard-cust-price-${index}`}
                />
              </div>
              <div className="flex items-end gap-1">
                <button type="button" onClick={() => setShowCalc(v => !v)} className="btn-ghost text-xs flex items-center gap-1 flex-1 justify-center" data-testid={`wizard-calc-${index}`}>
                  <Calculator size={12}/> {showCalc ? "Hide calc" : "Cost calc"}
                </button>
                <button type="button" onClick={() => setShowTee(v => !v)} className="btn-ghost text-xs flex items-center gap-1 flex-1 justify-center" data-testid={`wizard-tee-${index}`}>
                  <Shirt size={12}/> {showTee ? "Hide zones" : "Zones"}
                </button>
              </div>
            </div>

            {showCalc && (
              <div className="mt-3 p-2 border border-dashed border-[#EAE5DB] rounded">
                <PricingCalculator
                  initialMethod={(item.customization || "").toLowerCase().includes("embroid") ? "embroidery"
                    : (item.customization || "").toLowerCase().includes("screen") ? "screen" : "dtf"}
                  onChange={cost => setField("customization_price", Math.round(cost * 100) / 100)}
                  onClose={() => setShowCalc(false)}
                />
              </div>
            )}
            {showTee && (
              <div className="mt-3 p-2 border border-dashed border-[#EAE5DB] rounded">
                <TShirtDiagram
                  value={item.print_locations || {}}
                  onChange={v => setField("print_locations", v)}
                  onCost={cost => setField("customization_price", Math.round(cost * 100) / 100)}
                />
              </div>
            )}

            <div className="mt-3">
              <div className="label-tiny mb-1">Other customization <span className="normal-case text-[10px]">(free-text)</span></div>
              <input
                className="input-field mb-2"
                placeholder='e.g. "reflective tape", "sublimation panel"'
                value={item.other_customization || ""}
                onChange={e => setField("other_customization", e.target.value)}
                data-testid={`wizard-other-cust-${index}`}
              />
              <div className="label-tiny mb-1">Other customization ₹/pc</div>
              <input
                type="number"
                className="input-field"
                placeholder="0"
                value={item.other_customization_cost ?? 0}
                onChange={e => setField("other_customization_cost", Number(e.target.value) || 0)}
                data-testid={`wizard-other-cust-cost-${index}`}
              />
              <p className="text-[10px] text-[#75706A] mt-1">Both the customization ₹/pc and this other cost are added on top of the Unit ₹ for line total.</p>
            </div>
          </>
        )}

        {STEPS[step].key === "pricing" && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="label-tiny mb-1">Unit ₹ (base)</div>
                <input type="number" className="input-field"
                  value={item.unit_price || ""}
                  onChange={e => setField("unit_price", Number(e.target.value) || 0)}
                  data-testid={`wizard-unit-${index}`}
                />
              </div>
              <div>
                <div className="label-tiny mb-1">3XL+ ₹/pc</div>
                <input type="number" className="input-field"
                  value={item.oversize_surcharge ?? 0}
                  onChange={e => setField("oversize_surcharge", Number(e.target.value) || 0)}
                  data-testid={`wizard-over-${index}`}
                />
              </div>
              <div>
                <div className="label-tiny mb-1">HSN code</div>
                <input className="input-field"
                  value={item.hsn_code || ""}
                  onChange={e => setField("hsn_code", e.target.value)}
                  data-testid={`wizard-hsn-${index}`}
                />
              </div>
              <div>
                <div className="label-tiny mb-1">GST %</div>
                <input type="number" step="1" className="input-field"
                  value={item.gst_percent ?? 5}
                  onChange={e => setField("gst_percent", Number(e.target.value) || 0)}
                  data-testid={`wizard-gst-${index}`}
                />
              </div>
            </div>
          </>
        )}

        {STEPS[step].key === "review" && (
          <div className="text-sm space-y-1.5" data-testid={`wizard-review-${index}`}>
            <SummaryRow label="Product" value={<b>{item.product || "—"}</b>} />
            {item.color && <SummaryRow label="Color" value={item.color} />}
            <SummaryRow label="Qty" value={<b>{item.qty || 0}</b>} />
            {Object.keys(item.sizes || {}).length > 0 && (
              <SummaryRow label="Sizes" value={Object.entries(item.sizes).map(([s, n]) => `${s}·${n}`).join("  ")} />
            )}
            {item.other_size && <SummaryRow label="Other size" value={item.other_size} />}
            <SummaryRow label="Unit ₹" value={inr(item.unit_price || 0)} />
            {Number(item.customization_price) > 0 && <SummaryRow label={`Cust (${item.customization || "—"})`} value={`${inr(item.customization_price)} × ${item.qty}`} />}
            {Number(item.other_customization_cost) > 0 && <SummaryRow label={`Other (${item.other_customization || "—"})`} value={`${inr(item.other_customization_cost)} × ${item.qty}`} />}
            {Number(item.oversize_surcharge) > 0 && oversizeCount(item) > 0 && (
              <SummaryRow label="3XL+ surcharge" value={`${inr(item.oversize_surcharge)} × ${oversizeCount(item)} pcs`} />
            )}
            <SummaryRow label={`GST @ ${item.gst_percent ?? 5}%`} value="applied on final review" />
            <div className="pt-2 mt-2 border-t border-[#EAE5DB] flex justify-between text-base">
              <span className="font-medium">Line total</span>
              <span className="mono font-bold">{inr(lineTotal)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-[#EAE5DB] bg-white">
        <button type="button" disabled={step === 0} onClick={back} className="btn-ghost text-xs flex items-center gap-1 disabled:opacity-40" data-testid={`wizard-back-${index}`}>
          <ChevronLeft size={13}/> Back
        </button>
        <div className="text-[11px] text-[#75706A] mono">Step {step + 1} of {STEPS.length}</div>
        {step < STEPS.length - 1 ? (
          <button type="button" disabled={!stepValid} onClick={next} className="btn-primary text-xs flex items-center gap-1 disabled:opacity-40" data-testid={`wizard-next-${index}`}>
            Next <ChevronRight size={13}/>
          </button>
        ) : (
          <span className="text-xs text-[#4A7258] flex items-center gap-1"><Check size={13}/> Done</span>
        )}
      </div>
    </div>
  );
}

const SummaryRow = ({ label, value }) => (
  <div className="flex justify-between gap-2">
    <span className="text-[#75706A]">{label}</span>
    <span className="text-right">{value}</span>
  </div>
);
